import React from 'react';
import { Printer } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { printPdfFromUrl } from '../utils/printPdf';

const PRINT_ERROR = 'Drucken nicht möglich – bitte herunterladen.';

type Props = {
  url: string;
  /** `label`: „Drucken“ mit Icon (neben einem beschrifteten Download-Button); `icon`: nur Icon. */
  variant?: 'label' | 'icon';
  className?: string;
};

/** Öffnet den Druckdialog des Browsers für ein PDF (siehe `printPdfFromUrl`). */
export function PdfPrintButton({ url, variant = 'label', className }: Props) {
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [url]);

  const onClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await printPdfFromUrl(url);
    } catch (err) {
      console.error('[PdfPrintButton]', err);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const title = failed ? PRINT_ERROR : 'Drucken';
  const icon = busy ? (
    <LoadingSpinner size="sm" />
  ) : (
    <Printer className={`h-4 w-4 ${failed ? 'text-red-600' : ''}`} aria-hidden />
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
      className={`disabled:cursor-wait ${className ?? ''}`.trim()}
    >
      {icon}
      {variant === 'label' ? <span>{failed ? 'Fehler beim Drucken' : 'Drucken'}</span> : null}
    </button>
  );
}
